import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  CHANNEL_SHORT_NAME_MAP,
  CHANNEL_STATUS,
  IChannel,
  PROTOCOL,
  TChannelStatus,
} from '@/entities/channel';
import { TextField } from '@mui/material';

import { Box } from '@/shared/ui';
import { refreshMaxPersonalQr } from '@/shared/api/channels/maxPersonal';
import { Dialog, TDialogBase } from '@/shared/ui/Dialog';
import { Button } from '@/shared/ui/lib/Button';
import { Loader } from '@/shared/ui/Loader';

import { useMaxPersonal2fa } from '../../model/maxPersonal/useMaxPersonal2fa';
import { useMaxPersonalConnect } from '../../model/maxPersonal/useMaxPersonalConnect';
import { useChannelByIdImperative } from '../../model/useChannelByIdImperative';
import { ChannelIconWithLabel } from '../ChannelIconWithLabel';

const POLL_ATTEMPTS = 90; // ~3 min at 2s
const POLL_INTERVAL_MS = 2000;

const wait = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

const isAuthorizedStatus = (s?: TChannelStatus) =>
  s === CHANNEL_STATUS.AUTHORIZED ||
  s === CHANNEL_STATUS.READY ||
  s === CHANNEL_STATUS.CONTACTS_SYNCED ||
  s === CHANNEL_STATUS.CHATS_SYNCED ||
  s === CHANNEL_STATUS.ACTIVE;

const needsPasswordStatus = (s?: TChannelStatus) =>
  s === CHANNEL_STATUS.PASSWORD_REQUIRED;

type TMaxPersonalAuthDialogProps = TDialogBase & {
  channel: IChannel;
  onAuthorize?: () => void;
};

type Step = 'qr' | 'password' | 'done';

export const MaxPersonalAuthDialog = ({
  open,
  onClose,
  channel,
  onAuthorize,
  ...otherProps
}: TMaxPersonalAuthDialogProps) => {
  const { t } = useTranslation();
  const { connect, data, isPending: isConnecting, error: connectError, reset } = useMaxPersonalConnect();
  const { submit, isPending: is2faPending } = useMaxPersonal2fa();
  const { fetchChannelById } = useChannelByIdImperative();

  const [step, setStep] = useState<Step>('qr');
  const [password, setPassword] = useState('');
  const [qrBase64, setQrBase64] = useState<string | undefined>();
  const startedRef = useRef(false);

  // Reset internal state when dialog reopens.
  useEffect(() => {
    if (!open) {
      startedRef.current = false;
      setStep('qr');
      setPassword('');
      setQrBase64(undefined);
      reset();
    }
  }, [open, reset]);

  // Kick off connect on first open.
  useEffect(() => {
    if (!open || startedRef.current) return;
    if (channel.protocol !== PROTOCOL.MAX_PERSONAL) return;
    startedRef.current = true;
    void connect(channel.id, channel.name).catch(() => {
      /* surfaced via notification in hook */
    });
  }, [open, channel.id, channel.name, channel.protocol, connect]);

  // Poll channel status for transitions.
  useEffect(() => {
    if (!open || !data) return;
    if (data.qrBase64) setQrBase64(data.qrBase64);
    let cancelled = false;
    const poll = async () => {
      for (let i = 0; i < POLL_ATTEMPTS && !cancelled; i++) {
        if (!data.qrBase64 && !qrBase64) {
          const qr = await refreshMaxPersonalQr(channel.id).catch(() => null);
          if (cancelled) return;
          if (qr?.qrBase64) setQrBase64(qr.qrBase64);
        }
        const ch = await fetchChannelById(channel.id).catch(() => null);
        const s = ch?.status as TChannelStatus | undefined;
        if (cancelled) return;
        if (needsPasswordStatus(s)) {
          setStep('password');
          return;
        }
        if (isAuthorizedStatus(s)) {
          setStep('done');
          onAuthorize?.();
          return;
        }
        await wait(POLL_INTERVAL_MS);
      }
    };
    void poll();
    return () => {
      cancelled = true;
    };
  }, [open, data, qrBase64, channel.id, fetchChannelById, onAuthorize]);

  const handlePasswordSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!password) return;
    await submit(channel.id, password);
    // After 2FA, re-enter polling: backend transitions to authorized once
    // the password is accepted.
    setPassword('');
    setStep('qr');
  };

  const handleRetryConnect = () => {
    reset();
    setQrBase64(undefined);
    startedRef.current = true;
    void connect(channel.id, channel.name).catch(() => {
      /* surfaced via notification + connectError */
    });
  };

  const renderQrStep = () => {
    if (isConnecting) {
      return <Loader containerSize="small" />;
    }
    if (connectError) {
      const msg =
        connectError?.data?.details ||
        connectError?.message ||
        t(
          'channels:maxPersonal.connectFailed',
          'Не удалось подключить канал. Попробуйте позже.'
        );
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            py: 2,
          }}
        >
          <Box sx={{ color: 'error.main', textAlign: 'center' }}>{msg}</Box>
          <Button variant="contained" onClick={handleRetryConnect}>
            {t('channels:maxPersonal.retry', 'Попробовать снова')}
          </Button>
        </Box>
      );
    }
    if (!data) {
      return <Loader containerSize="small" />;
    }
    if (data.status === 'authorized') {
      return (
        <Box>
          {t(
            'channels:maxPersonal.alreadyAuthorized',
            'Аккаунт уже авторизован'
          )}
        </Box>
      );
    }
    if (!qrBase64) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            py: 2,
          }}
        >
          <Loader containerSize="small" />
          <Box sx={{ textAlign: 'center', color: 'text.secondary' }}>
            {t(
              'channels:maxPersonal.qrPreparing',
              'Инстанс создается, QR-код появится автоматически. Обычно это занимает 1-2 минуты.'
            )}
          </Box>
        </Box>
      );
    }
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Box
          component="img"
          src={`data:image/png;base64,${qrBase64}`}
          alt="QR"
          sx={{ width: 256, height: 256 }}
        />
        <Box sx={{ textAlign: 'center', color: 'text.secondary' }}>
          {t(
            'channels:maxPersonal.scanWithApp',
            'Откройте MAX на телефоне → Профиль → Устройства → Войти через QR'
          )}
        </Box>
      </Box>
    );
  };

  const renderPasswordStep = () => (
    <Box
      component="form"
      onSubmit={handlePasswordSubmit}
      sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
    >
      <Box>
        {t(
          'channels:maxPersonal.passwordPrompt',
          'Введите 2FA-пароль вашего MAX-аккаунта'
        )}
      </Box>
      <TextField
        type="password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        autoFocus
        fullWidth
      />
      <Button
        variant="contained"
        type="submit"
        loading={is2faPending}
        disabled={!password}
      >
        {t('channels:maxPersonal.submitPassword', 'Подтвердить')}
      </Button>
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t(
        'channels:maxPersonal.authTitle',
        'Подключение MAX Personal'
      )}
      additionalTitleSlot={
        <ChannelIconWithLabel
          label={CHANNEL_SHORT_NAME_MAP[PROTOCOL.MAX_PERSONAL]}
          protocol={PROTOCOL.MAX_PERSONAL}
        />
      }
      leftButtonSlot={
        <Button variant="outlined" onClick={() => onClose?.()}>
          {t('channels:buttons.closeModal')}
        </Button>
      }
      {...otherProps}
    >
      <Box sx={{ maxWidth: 670, width: '100%', minHeight: 320 }}>
        {step === 'password' ? renderPasswordStep() : renderQrStep()}
      </Box>
    </Dialog>
  );
};
